import { MissMFCResponse } from '../../types'
import { SaveResult } from '../../types/state'
import { locales } from '../locales/en'
import { getMissMyFreeCams, setMissMFCOptOut } from '../mfcLogic/api/missmfc'
import { ROUTESTATE } from '../state'
import { pubSubRouter } from '../state/pubSub'
import { loadWithRetry } from '../utilities/dynamicWithRetry'

export const MISS_MFC_PAGE = 'miss-mfc'

/**
 * a promise `use()` can read synchronously
 */
const settled = <T>(value: T) =>
    Object.assign(Promise.resolve(value), { status: 'fulfilled', value })

const cached: { latest: Promise<MissMFCResponse> | null } = { latest: null }

const fetchRankings = async () => {
    const rankings = await loadWithRetry(getMissMyFreeCams)
    if (rankings.err) throw new Error(String(rankings.err))
    return rankings
}

export const reloadMissMFCRankings = async () => {
    try {
        const promise = settled(await fetchRankings())
        cached.latest = promise
        ROUTESTATE.preloadCache.set(MISS_MFC_PAGE, {
            promise,
            timestamp: Date.now(),
        })
        pubSubRouter.emit('preload', MISS_MFC_PAGE, true)
        return true
    } catch (error) {
        console.error('[MissMFC] failed to reload rankings', error)
        return false
    }
}

export const loadMissMFCRankings = () => {
    if (cached.latest !== null) {
        void reloadMissMFCRankings()
        return cached.latest
    }
    const request = fetchRankings().then(
        (rankings) => {
            cached.latest = settled(rankings)
            return rankings
        },
        (error) => {
            cached.latest = null
            throw error
        },
    )
    cached.latest = request
    return request
}

/**
 * saves the Anonymous setting
 * then re-reads the rankings so the UI reflects the stored value
 * @param optout
 * @returns
 */
export const saveMissMFCOptOut = async (optout: 0 | 1): Promise<SaveResult> => {
    const strings = locales.missMFC.anonymous
    try {
        const token = (await cached.latest)?.result.token
        const response = await setMissMFCOptOut(optout, token)
        if (response.err) {
            return {
                ok: false,
                message: response.errmsg || strings.errorSaveFailed,
            }
        }
        const message = [response.result ?? []].flat().join(' ')
        if (message.includes('fifth')) {
            return { ok: false, message: strings.errorAfterFifth }
        }
        return (await reloadMissMFCRankings())
            ? { ok: true }
            : { ok: false, message: strings.errorRefreshFailed }
    } catch (error) {
        console.error('[MissMFC] failed to save anonymous setting', error)
        return { ok: false, message: strings.errorSaveFailed }
    }
}
